import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { useMutation, useQuery } from '@tanstack/react-query';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import Checkbox from 'components/shared/form/Checkbox';
import FormHeader from 'components/shared/form/FormHeader';
import FormSection from 'components/shared/form/FormSection';
import { useProfile } from 'context/profile';
import type { ProductAccess as ProductAccessValues } from 'models/flexible/Client';
import { PERMISSIONS } from 'models/flexible/Profile';
import { adminShowClient, adminUpdateClient } from 'services/api/admin/clients';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';

type ProductKey = keyof ProductAccessValues;
type Toggle = { enabled: boolean };
type ProductValue = Toggle & { [featureKey: string]: Toggle | boolean };
type Values = Record<ProductKey, ProductValue>;

// A product's `features` are settings nested in its value (`{ enabled, <feature>: { enabled } }`) that only
// apply while the product is on.
export const PRODUCTS: { key: ProductKey; label: string; features?: { key: string; label: string }[] }[] = [
  { key: 'traditionalOlio', label: 'Care Coordination (Traditional Olio)' },
  { key: 'olioSummarization', label: 'Summarization Workspace' },
  { key: 'referralSummarization', label: 'Referral Summarization' },
  { key: 'dischargeCalendar', label: 'Discharge Calendar' },
  // Two separate products, two separate keys — the legacy Admissions review and the newer
  // Inbound Referrals. Disambiguated in the label only; the key must not change (OLIO-2905).
  { key: 'admissions', label: 'Admissions (legacy)' },
  { key: 'inboundReferrals', label: 'Inbound Referrals', features: [{ key: 'tourTracking', label: 'Tour Tracking' }] },
  { key: 'preferredProviders', label: 'Preferred Providers' },
];

function productValue(saved: Partial<Record<string, any>> | undefined, features: { key: string }[] = []): ProductValue {
  return {
    enabled: saved?.enabled ?? false,
    ...Object.fromEntries(features.map(({ key }) => [key, { enabled: saved?.[key]?.enabled ?? false }])),
  };
}

export default function ProductAccess() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToastActions();
  const { profile } = useProfile();
  const disabled = !profile.has(PERMISSIONS.adminClientEdit);

  const { data: client, isLoading } = useQuery({
    queryKey: ['adminClient', id, 'productAccess'],
    queryFn: ({ signal }) => adminShowClient(id!, signal),
    enabled: !!id,
  });

  const [values, setValues] = useState<Values | null>(null);
  const [serverValues, setServerValues] = useState<Values | null>(null);

  useEffect(() => {
    if (client?.productAccess) {
      const loaded = Object.fromEntries(
        PRODUCTS.map(({ key, features }) => [key, productValue(client.productAccess[key], features)])
      ) as Values;
      setValues(loaded);
      setServerValues(loaded);
    }
  }, [client]);

  const isDirty = JSON.stringify(values) !== JSON.stringify(serverValues);

  const mutation = useMutation({
    mutationFn: (payload: Partial<ProductAccessValues>) => adminUpdateClient(id!, payload),
    onSuccess: () => {
      addToast({ text: 'Product access successfully updated' });
      navigate('/admin/clients');
    },
    onError: (e: any) => {
      const msg = e?.response?.data?.message || 'An error occurred while updating product access';
      addToast({ text: msg });
    },
  });

  const handleToggle = (key: ProductKey, features?: { key: string }[]) => {
    if (!values) return;
    const value = values[key];
    setValues({ ...values, [key]: value.enabled ? productValue(undefined, features) : { ...value, enabled: true } });
  };

  const handleFeatureToggle = (key: ProductKey, featureKey: string) => {
    if (!values) return;
    const value = values[key];
    const feature = value[featureKey] as Toggle;
    setValues({ ...values, [key]: { ...value, [featureKey]: { enabled: !feature.enabled } } });
  };

  const handleSave = () => {
    if (!values) return;
    mutation.mutate(values as unknown as ProductAccessValues);
  };

  const goBack = () => navigate('/admin/clients');

  if (isLoading) return <CenteredSpinner />;

  return (
    <AdminFormPage>
      <FormHeader title='Product Access' subtitle={client?.name} />
      <StyledFormSection>
        {PRODUCTS.map(({ key, label, features }) => (
          <React.Fragment key={key}>
            <StyledCheckbox
              checked={values?.[key]?.enabled ?? false}
              onChange={() => handleToggle(key, features)}
              label={label}
              labelSize='14px'
              checkedColor={colors.blue100}
              disabled={disabled}
              size={14}
            />
            {features?.map((feature) => (
              <NestedCheckbox
                key={feature.key}
                checked={(values?.[key]?.[feature.key] as Toggle | undefined)?.enabled ?? false}
                onChange={() => handleFeatureToggle(key, feature.key)}
                label={feature.label}
                labelSize='14px'
                checkedColor={colors.blue100}
                disabled={disabled || !values?.[key]?.enabled}
                size={14}
              />
            ))}
          </React.Fragment>
        ))}
      </StyledFormSection>
      <ButtonGroup style={{ justifyContent: 'flex-end', marginTop: 24 }}>
        <Button variant='ghost' onClick={goBack}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={disabled || !isDirty || mutation.isPending} loading={mutation.isPending}>
          Update Product Access
        </Button>
      </ButtonGroup>
    </AdminFormPage>
  );
}

const CenteredSpinner = styled(CircularProgress)`
  margin: 64px auto;
  display: block;
`;

const StyledFormSection = styled(FormSection)`
  display: flex;
  flex-direction: column;
`;

const StyledCheckbox = styled(Checkbox)`
  margin-top: 16px;

  &:first-child {
    margin-top: 8px;
  }

  &:not(:last-child) {
    margin-bottom: 16px;
  }
`;

const NestedCheckbox = styled(StyledCheckbox)`
  margin-left: 24px;
`;
